/**
 * „A apărut o versiune nouă — reîmprospătați pagina", ca banda lor.
 *
 * Două semne: un modul încărcat leneș care nu mai există pe server (o
 * publicare a înlocuit fișierele, iar Vite anunță `vite:preloadError`) și,
 * la fiecare zece minute, scriptul principal din `index.html` care nu mai e
 * cel cu care s-a deschis pagina. Nimic nu reîmprospătează singur: un medic
 * poate avea o ciornă deschisă.
 */
export type Listener = () => void;

const listeners = new Set<Listener>();
let stale = false;

function announce() {
  if (stale) return;
  stale = true;
  for (const l of listeners) l();
}

export function onNewVersion(listener: Listener): () => void {
  listeners.add(listener);
  if (stale) listener();
  return () => listeners.delete(listener);
}

export function isStale(): boolean {
  return stale;
}

/**
 * Eroarea unui modul încărcat leneș care nu mai există pe server: o publicare
 * i-a înlocuit fișierul cât pagina era deschisă. Fiecare browser o spune altfel.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /dynamically imported module|Importing a module script failed|Unable to preload CSS|error loading dynamically imported/i.test(
    message,
  );
}

/** Scriptul principal dintr-un HTML: `/assets/index-HASH.js`. */
export function mainScriptOf(html: string): string | null {
  const m = /<script[^>]+type="module"[^>]+src="([^"]+)"/.exec(html);
  return m?.[1] ?? null;
}

export function watchNewVersion(everyMs = 10 * 60_000): () => void {
  // Announce only; the import still fails. `preventDefault()` here told Vite
  // not to throw, and the import then resolved to `undefined`: `React.lazy`
  // read `.default` off it and every screen opened after a deploy fell on
  // „Aplicația a întâmpinat o eroare” instead of this banner. The failure now
  // reaches the boundary, which recognises it (`isChunkLoadError`) and says a
  // new version is out.
  const onPreload = () => announce();
  globalThis.addEventListener('vite:preloadError', onPreload);
  const current = mainScriptOf(document.documentElement.outerHTML);
  const timer =
    current && import.meta.env.PROD
      ? globalThis.setInterval(() => {
          void fetch('/', { cache: 'no-store' })
            .then((r) => (r.ok ? r.text() : null))
            .then((html) => {
              const next = html ? mainScriptOf(html) : null;
              if (next && next !== current) announce();
            })
            .catch(() => undefined);
        }, everyMs)
      : null;
  return () => {
    globalThis.removeEventListener('vite:preloadError', onPreload);
    if (timer !== null) globalThis.clearInterval(timer);
  };
}
